/**
 * SegmentedRadioGroup
 * A WAI-ARIA radio group drawn as buttons (role="radio", roving tabindex, arrow-key selection).
 * Shared by the email inbox type filter (chips), the compose recipient type and the link
 * dialog's Tenant | Vendor choice (segmented control).
 */

import { useRef } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  testId: string;
}

interface SegmentedRadioGroupProps<T extends string> {
  /** Accessible name of the group (e.g. "Show", "Link to"). */
  ariaLabel: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  variant?: 'segmented' | 'chips';
  testId?: string;
  className?: string;
}

const CONTAINER: Record<'segmented' | 'chips', string> = {
  segmented: 'inline-flex rounded-md border border-border bg-muted p-0.5',
  chips: 'flex flex-wrap gap-2',
};

const OPTION: Record<'segmented' | 'chips', { base: string; on: string; off: string }> = {
  segmented: {
    base: 'min-h-[44px] px-4 text-sm font-medium rounded transition-colors',
    on: 'bg-primary text-primary-foreground shadow-sm',
    off: 'text-muted-foreground hover:text-foreground',
  },
  chips: {
    base: 'min-h-[44px] px-4 text-sm font-medium rounded-full border transition-colors',
    on: 'border-primary bg-primary/10 text-primary',
    off: 'border-border bg-card text-foreground hover:bg-accent',
  },
};

export function SegmentedRadioGroup<T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  variant = 'segmented',
  testId,
  className = '',
}: SegmentedRadioGroupProps<T>): JSX.Element {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const styles = OPTION[variant];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const current = options.findIndex((o) => o.value === value);
    let next: number;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (current + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (current - 1 + options.length) % options.length;
    else return;
    e.preventDefault();
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      className={`${CONTAINER[variant]} ${className}`}
      onKeyDown={handleKeyDown}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            data-testid={option.testId}
            className={`${styles.base} ${selected ? styles.on : styles.off}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
